import {
  AI_EMBEDDING_DIMENSIONS,
  AI_HARDWARE_PROFILES,
  AI_KEEP_ALIVE_MAX,
  AI_KEEP_ALIVE_MIN,
  AI_MAX_PASSAGES_MAX,
  AI_MAX_PASSAGES_MIN,
  AI_NUM_CTX_MAX,
  AI_NUM_CTX_MIN,
  AI_NUM_PREDICT_MAX,
  AI_NUM_PREDICT_MIN,
  AI_PASSAGE_CHARS_MAX,
  AI_PASSAGE_CHARS_MIN,
  aiProviderUsesApiKey,
  matchingHardwareProfile,
  type AiConnection,
  type AiConnectionProbe,
  type AiHardwareProfile,
  type AiProviderName,
  type UpdateAiConnectionRequest,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';

import { useVoiceCopy } from '@/platform/voice/use-voice';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { AiAdminAgentsBlock } from '@/platform/features/ai/ai-admin-agents-block';
import {
  useAiConnection,
  useProbeAiConnection,
  useUpdateAiConnection,
} from '@/platform/features/ai/use-ai-connection';

const OLLAMA_BASE_URL = 'http://127.0.0.1:11434';
const OPENAI_BASE_URL = 'https://api.openai.com/v1';
const GROK_BASE_URL = 'https://api.x.ai/v1';
const OLLAMA_CHAT_MODEL = 'llama3.2';
const OPENAI_CHAT_MODEL = 'gpt-4.1-mini';
const GROK_CHAT_MODEL = 'grok-4.7';
const ASSIST_USES_CHAT = '__chat_model__';
const PROVIDER_DEFAULTS: Record<AiProviderName, { baseUrl: string; chatModel: string }> = {
  ollama: { baseUrl: OLLAMA_BASE_URL, chatModel: OLLAMA_CHAT_MODEL },
  openai: { baseUrl: OPENAI_BASE_URL, chatModel: OPENAI_CHAT_MODEL },
  grok: { baseUrl: GROK_BASE_URL, chatModel: GROK_CHAT_MODEL },
};
const CHAT_TIMEOUT_MIN = 5_000;
const CHAT_TIMEOUT_MAX = 180_000;
const EMBED_TIMEOUT_MIN = 1_000;
const EMBED_TIMEOUT_MAX = 120_000;

type Draft = {
  enabled: boolean;
  intranetEnabled: boolean;
  provider: AiProviderName;
  baseUrl: string;
  chatModel: string;
  assistModel: string;
  embeddingModel: string;
  chatTimeoutMs: string;
  embedTimeoutMs: string;
  numCtx: string;
  numPredict: string;
  maxPassages: string;
  passageChars: string;
  allowRefetch: boolean;
  keepAliveSeconds: string;
};

export function AiAdminPage() {
  usePageTitle('KI');
  const connection = useAiConnection();

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="KI"
        description="Anschluss an Ollama, OpenAI oder Grok. Helpdesk und Intranet nutzen denselben Anschluss."
      />

      {connection.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Anschluss wird geladen …
        </p>
      )}

      {connection.isError && (
        <p role="alert" className="text-sm text-destructive">
          {connection.error instanceof ApiRequestError
            ? connection.error.message
            : 'Der KI-Anschluss konnte nicht geladen werden.'}
        </p>
      )}

      {connection.data && (
        <AiConnectionForm key={connection.data.updatedAt} connection={connection.data} />
      )}

      <AiAdminAgentsBlock />
    </section>
  );
}

function AiConnectionForm({ connection }: { connection: AiConnection }) {
  const update = useUpdateAiConnection();
  const probe = useProbeAiConnection();
  const [draft, setDraft] = useState<Draft>(() => toDraft(connection));
  const [apiKey, setApiKey] = useState('');
  const [clearApiKey, setClearApiKey] = useState(false);
  const [probeResult, setProbeResult] = useState<AiConnectionProbe | null>(null);

  const cloud = aiProviderUsesApiKey(draft.provider);
  const cloudName = draft.provider === 'grok' ? 'Grok' : 'OpenAI';
  const defaults = PROVIDER_DEFAULTS[draft.provider];
  const languageModels =
    draft.provider === 'grok' && connection.provider === 'grok'
      ? (probeResult?.languageModels ?? [])
      : [];
  const pickModels = languageModels.length > 0 && draft.chatModel.trim().length > 0;

  function patch(next: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...next }));
  }

  function changeProvider(provider: AiProviderName) {
    setDraft((current) => applyProvider(current, provider));
    if (provider === 'ollama') {
      setApiKey('');
      setClearApiKey(false);
    }
  }

  async function save() {
    let body: UpdateAiConnectionRequest;
    try {
      body = toRequest(draft, apiKey, clearApiKey);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Die Eingaben sind unvollständig.');
      return;
    }

    try {
      await update.mutateAsync(body);
      setApiKey('');
      setClearApiKey(false);
      setProbeResult(null);
      toast.success('Der KI-Anschluss wurde gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Die Änderung wurde nicht gespeichert.',
      );
    }
  }

  async function check() {
    try {
      const result = await probe.mutateAsync();
      setProbeResult(result);
    } catch (error) {
      setProbeResult(null);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Verbindung konnte nicht geprüft werden.',
      );
    }
  }

  return (
    <form
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle>Anschluss</CardTitle>
          <CardDescription>
            Ein Speichern schreibt den ganzen Anschluss. Ein leeres Schlüssel-Feld behält den
            gespeicherten Schlüssel.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ToggleRow
            id="ai-enabled"
            label="KI-Anschluss"
            description="Schaltet den KI-Anschluss für Helpdesk und Intranet."
            checked={draft.enabled}
            disabled={update.isPending}
            onCheckedChange={(enabled) => patch({ enabled })}
          />
          <ToggleRow
            id="ai-intranet"
            label="Intranet-Assistent"
            description="Entwurf und Fragen im Intranet. Die Helpdesk-KI bleibt unter den Helpdesk-Modulen."
            checked={draft.intranetEnabled}
            disabled={update.isPending}
            onCheckedChange={(intranetEnabled) => patch({ intranetEnabled })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="ai-provider">Anbieter</Label>
              <Select
                value={draft.provider}
                disabled={update.isPending}
                onValueChange={(value) => changeProvider(value as AiProviderName)}
              >
                <SelectTrigger id="ai-provider" className="mt-2 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ollama">Ollama</SelectItem>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="grok">Grok</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field
              id="ai-base-url"
              label="Basis-URL"
              value={draft.baseUrl}
              disabled={update.isPending}
              placeholder={defaults.baseUrl}
              hint={
                draft.provider === 'ollama'
                  ? `Zum Beispiel ${OLLAMA_BASE_URL}`
                  : `Standard ${defaults.baseUrl}`
              }
              onChange={(baseUrl) => patch({ baseUrl })}
            />
            {pickModels ? (
              <ModelSelect
                id="ai-chat-model"
                label="Chat-Modell"
                value={draft.chatModel}
                disabled={update.isPending}
                hint={`Vorschlag ${defaults.chatModel}`}
                options={modelChoices(languageModels, draft.chatModel, false)}
                onChange={(chatModel) => patch({ chatModel })}
              />
            ) : (
              <Field
                id="ai-chat-model"
                label="Chat-Modell"
                value={draft.chatModel}
                disabled={update.isPending}
                placeholder={defaults.chatModel}
                hint={`Vorschlag ${defaults.chatModel}`}
                onChange={(chatModel) => patch({ chatModel })}
              />
            )}
            {pickModels ? (
              <ModelSelect
                id="ai-assist-model"
                label="Assist-Modell"
                value={draft.assistModel.length > 0 ? draft.assistModel : ASSIST_USES_CHAT}
                disabled={update.isPending}
                hint="Helpdesk-Ticket-Hilfe. Leer verwendet das Chat-Modell."
                options={modelChoices(languageModels, draft.assistModel, true)}
                onChange={(assistModel) =>
                  patch({ assistModel: assistModel === ASSIST_USES_CHAT ? '' : assistModel })
                }
              />
            ) : (
              <Field
                id="ai-assist-model"
                label="Assist-Modell"
                value={draft.assistModel}
                disabled={update.isPending}
                placeholder="Leer = Chat-Modell"
                hint="Helpdesk-Ticket-Hilfe. Leer verwendet das Chat-Modell."
                onChange={(assistModel) => patch({ assistModel })}
              />
            )}
            <Field
              id="ai-embedding-model"
              label="Embedding-Modell"
              value={draft.embeddingModel}
              disabled={update.isPending || cloud}
              hint={
                cloud
                  ? `Bei ${cloudName} bleibt die Vektorsuche aus. Die Wissenssuche nutzt Volltext. Die Dimension bleibt ${AI_EMBEDDING_DIMENSIONS}.`
                  : `Dimension ${AI_EMBEDDING_DIMENSIONS}.`
              }
              onChange={(embeddingModel) => patch({ embeddingModel })}
            />
            {draft.provider === 'ollama' && (
              <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground sm:col-span-2">
                <p className="font-medium text-foreground">Empfohlene Ollama-Namen</p>
                <p className="mt-2">
                  Modelle werden hier nur eingetragen. Das System lädt sie nicht automatisch (
                  <code className="text-xs">ollama pull</code> manuell auf dem Host).
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  <li>
                    <strong>Minimalprofil</strong> (wenig RAM, z. B. 4 GB): Chat{' '}
                    <code className="text-xs">qwen2.5:1.5b</code>, Assistenz leer (nutzt Chat),
                    Embedding <code className="text-xs">nomic-embed-text</code>. Dazu unter
                    Erweiterte Einstellungen <strong>Wenig Speicher</strong> wählen. Ein kleines
                    Modell begrenzt das Fenster auch dann, wenn hier ein größeres Profil steht.
                  </li>
                  <li>
                    <strong>Serverprofil</strong>: Chat <code className="text-xs">qwen3:8b</code>,
                    Assistenz <code className="text-xs">qwen3:14b</code>, Embedding{' '}
                    <code className="text-xs">nomic-embed-text</code>.
                  </li>
                </ul>
              </div>
            )}
            <Field
              id="ai-chat-timeout"
              label="Chat-Timeout"
              value={draft.chatTimeoutMs}
              disabled={update.isPending}
              inputMode="numeric"
              hint={`${CHAT_TIMEOUT_MIN}–${CHAT_TIMEOUT_MAX} Millisekunden.`}
              onChange={(chatTimeoutMs) => patch({ chatTimeoutMs })}
            />
            <Field
              id="ai-embed-timeout"
              label="Embedding-Timeout"
              value={draft.embedTimeoutMs}
              disabled={update.isPending}
              inputMode="numeric"
              hint={`${EMBED_TIMEOUT_MIN}–${EMBED_TIMEOUT_MAX} Millisekunden.`}
              onChange={(embedTimeoutMs) => patch({ embedTimeoutMs })}
            />
          </div>

          {cloud && (
            <div className="space-y-2">
              <Label htmlFor="ai-api-key">API-Schlüssel</Label>
              <Input
                id="ai-api-key"
                type="password"
                name="ai-api-key"
                autoComplete="new-password"
                className="mt-2"
                value={apiKey}
                disabled={update.isPending}
                placeholder={
                  clearApiKey ? 'Wird entfernt' : connection.apiKeyConfigured ? 'Gespeichert' : ''
                }
                onChange={(event) => setApiKey(event.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                {clearApiKey
                  ? 'Der Schlüssel wird beim Speichern entfernt.'
                  : connection.apiKeyConfigured
                    ? 'Ein leeres Feld behält den gespeicherten Schlüssel.'
                    : `Für einen eingeschalteten ${cloudName}-Anschluss ist ein Schlüssel nötig.`}
              </p>
              {connection.apiKeyConfigured && !clearApiKey && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={update.isPending}
                  onClick={() => {
                    setApiKey('');
                    setClearApiKey(true);
                  }}
                >
                  Schlüssel entfernen
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AdvancedLimits
        draft={draft}
        disabled={update.isPending}
        onPatch={patch}
        onApplyProfile={(profile) => patch(profileDraft(profile))}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
          Speichern
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={probe.isPending || update.isPending}
          onClick={() => void check()}
        >
          {probe.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
          Verbindung prüfen
        </Button>
      </div>

      {probeResult && (
        <p role="status" className="text-sm">
          {describeProbe(probeResult, connection)}
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        Zuletzt geändert: {formatDateTime(connection.updatedAt)}. Die Prüfung gilt für den
        gespeicherten Anschluss.
      </p>
    </form>
  );
}

function AdvancedLimits({
  draft,
  disabled,
  onPatch,
  onApplyProfile,
}: {
  draft: Draft;
  disabled: boolean;
  onPatch: (next: Partial<Draft>) => void;
  onApplyProfile: (profile: AiHardwareProfile) => void;
}) {
  const copy = useVoiceCopy();
  const active = matchingHardwareProfile(limitsFromDraft(draft));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Erweiterte Einstellungen</CardTitle>
        <CardDescription>{copy('platform.ai.limits')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap gap-2">
          {AI_HARDWARE_PROFILES.map((profile) => (
            <Button
              key={profile.id}
              type="button"
              variant={active?.id === profile.id ? 'default' : 'outline'}
              disabled={disabled}
              onClick={() => onApplyProfile(profile)}
            >
              {profile.label}
            </Button>
          ))}
        </div>
        <ul className="space-y-1 text-sm text-muted-foreground">
          {AI_HARDWARE_PROFILES.map((profile) => (
            <li key={profile.id}>
              <span className="font-medium text-foreground">{profile.label}.</span>{' '}
              {profile.description}
            </li>
          ))}
        </ul>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="ai-num-ctx"
            label="Kontextfenster"
            value={draft.numCtx}
            disabled={disabled}
            inputMode="numeric"
            hint={`${AI_NUM_CTX_MIN}–${AI_NUM_CTX_MAX} Token. Darin liegen Frage und Fundstellen.`}
            onChange={(numCtx) => onPatch({ numCtx })}
          />
          <Field
            id="ai-num-predict"
            label="Antwortlänge"
            value={draft.numPredict}
            disabled={disabled}
            inputMode="numeric"
            hint={`${AI_NUM_PREDICT_MIN}–${AI_NUM_PREDICT_MAX} Token. Zu wenig schneidet den Satz ab.`}
            onChange={(numPredict) => onPatch({ numPredict })}
          />
          <Field
            id="ai-max-passages"
            label="Stellen pro Frage"
            value={draft.maxPassages}
            disabled={disabled}
            inputMode="numeric"
            hint={`${AI_MAX_PASSAGES_MIN}–${AI_MAX_PASSAGES_MAX} Ausschnitte aus den Wissensquellen.`}
            onChange={(maxPassages) => onPatch({ maxPassages })}
          />
          <Field
            id="ai-passage-chars"
            label="Zeichen pro Stelle"
            value={draft.passageChars}
            disabled={disabled}
            inputMode="numeric"
            hint={`${AI_PASSAGE_CHARS_MIN}–${AI_PASSAGE_CHARS_MAX} Zeichen je Ausschnitt.`}
            onChange={(passageChars) => onPatch({ passageChars })}
          />
          <Field
            id="ai-keep-alive"
            label="Modell im Speicher"
            value={draft.keepAliveSeconds}
            disabled={disabled}
            inputMode="numeric"
            hint={`${AI_KEEP_ALIVE_MIN}–${AI_KEEP_ALIVE_MAX} Sekunden, danach entlädt Ollama das Modell.`}
            onChange={(keepAliveSeconds) => onPatch({ keepAliveSeconds })}
          />
        </div>
        <ToggleRow
          id="ai-allow-refetch"
          label="Zweiter Versuch"
          description="Wenn die erste Antwort leer oder abgeschnitten ist und weitere Stellen da sind, fragt die KI einmal nach."
          checked={draft.allowRefetch}
          disabled={disabled}
          onCheckedChange={(allowRefetch) => onPatch({ allowRefetch })}
        />
      </CardContent>
    </Card>
  );
}

function ToggleRow({
  id,
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 space-y-1">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function ModelSelect({
  id,
  label,
  value,
  hint,
  disabled,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  hint: string;
  disabled: boolean;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="sm:col-span-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger id={id} className="mt-2 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  hint,
  disabled,
  placeholder,
  inputMode,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  hint: string;
  disabled: boolean;
  placeholder?: string;
  inputMode?: 'numeric';
  onChange: (value: string) => void;
}) {
  return (
    <div className="sm:col-span-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="mt-2"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        inputMode={inputMode}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
    </div>
  );
}

function toDraft(connection: AiConnection): Draft {
  return {
    enabled: connection.enabled,
    intranetEnabled: connection.intranetEnabled,
    provider: connection.provider,
    baseUrl: connection.baseUrl,
    chatModel: connection.chatModel,
    assistModel: connection.assistModel,
    embeddingModel: connection.embeddingModel,
    chatTimeoutMs: String(connection.chatTimeoutMs),
    embedTimeoutMs: String(connection.embedTimeoutMs),
    numCtx: String(connection.numCtx),
    numPredict: String(connection.numPredict),
    maxPassages: String(connection.maxPassages),
    passageChars: String(connection.passageChars),
    allowRefetch: connection.allowRefetch,
    keepAliveSeconds: String(connection.keepAliveSeconds),
  };
}

function profileDraft(profile: AiHardwareProfile): Partial<Draft> {
  return {
    numCtx: String(profile.numCtx),
    numPredict: String(profile.numPredict),
    maxPassages: String(profile.maxPassages),
    passageChars: String(profile.passageChars),
    allowRefetch: profile.allowRefetch,
    keepAliveSeconds: String(profile.keepAliveSeconds),
  };
}

function limitsFromDraft(draft: Draft) {
  return {
    numCtx: Number(draft.numCtx),
    numPredict: Number(draft.numPredict),
    maxPassages: Number(draft.maxPassages),
    passageChars: Number(draft.passageChars),
    allowRefetch: draft.allowRefetch,
    keepAliveSeconds: Number(draft.keepAliveSeconds),
  };
}

function applyProvider(current: Draft, provider: AiProviderName): Draft {
  const next = { ...current, provider };
  const foreign = (Object.keys(PROVIDER_DEFAULTS) as AiProviderName[]).filter(
    (name) => name !== provider,
  );
  const url = current.baseUrl.trim();
  if (url.length === 0 || foreign.some((name) => PROVIDER_DEFAULTS[name].baseUrl === url)) {
    next.baseUrl = PROVIDER_DEFAULTS[provider].baseUrl;
  }
  const model = current.chatModel.trim();
  if (model.length === 0 || foreign.some((name) => PROVIDER_DEFAULTS[name].chatModel === model)) {
    next.chatModel = PROVIDER_DEFAULTS[provider].chatModel;
  }
  return next;
}

function modelChoices(
  names: string[],
  current: string,
  includeChatFallback: boolean,
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const seen = new Set<string>();
  if (includeChatFallback) {
    options.push({ value: ASSIST_USES_CHAT, label: 'Leer = Chat-Modell' });
    seen.add(ASSIST_USES_CHAT);
  }
  for (const name of names) {
    if (seen.has(name)) {
      continue;
    }
    seen.add(name);
    options.push({ value: name, label: name });
  }
  const extra = current.trim();
  if (extra.length > 0 && !seen.has(extra)) {
    options.push({ value: extra, label: extra });
  }
  return options;
}

function toRequest(draft: Draft, apiKey: string, clearApiKey: boolean): UpdateAiConnectionRequest {
  const chatModel = draft.chatModel.trim();
  const embeddingModel = draft.embeddingModel.trim();
  if (!chatModel) {
    throw new Error('Bitte ein Chat-Modell angeben.');
  }
  if (!embeddingModel) {
    throw new Error('Bitte ein Embedding-Modell angeben.');
  }
  const body: UpdateAiConnectionRequest = {
    enabled: draft.enabled,
    intranetEnabled: draft.intranetEnabled,
    provider: draft.provider,
    baseUrl: draft.baseUrl.trim(),
    chatModel,
    assistModel: draft.assistModel.trim(),
    embeddingModel,
    embeddingDimensions: AI_EMBEDDING_DIMENSIONS,
    chatTimeoutMs: parseTimeout(
      draft.chatTimeoutMs,
      CHAT_TIMEOUT_MIN,
      CHAT_TIMEOUT_MAX,
      'Chat-Timeout',
    ),
    embedTimeoutMs: parseTimeout(
      draft.embedTimeoutMs,
      EMBED_TIMEOUT_MIN,
      EMBED_TIMEOUT_MAX,
      'Embedding-Timeout',
    ),
    numCtx: parseTimeout(draft.numCtx, AI_NUM_CTX_MIN, AI_NUM_CTX_MAX, 'Kontextfenster'),
    numPredict: parseTimeout(
      draft.numPredict,
      AI_NUM_PREDICT_MIN,
      AI_NUM_PREDICT_MAX,
      'Antwortlänge',
    ),
    maxPassages: parseTimeout(
      draft.maxPassages,
      AI_MAX_PASSAGES_MIN,
      AI_MAX_PASSAGES_MAX,
      'Stellen pro Frage',
    ),
    passageChars: parseTimeout(
      draft.passageChars,
      AI_PASSAGE_CHARS_MIN,
      AI_PASSAGE_CHARS_MAX,
      'Zeichen pro Stelle',
    ),
    allowRefetch: draft.allowRefetch,
    keepAliveSeconds: parseTimeout(
      draft.keepAliveSeconds,
      AI_KEEP_ALIVE_MIN,
      AI_KEEP_ALIVE_MAX,
      'Modell im Speicher',
    ),
  };
  const typedKey = apiKey.trim();
  if (aiProviderUsesApiKey(draft.provider) && typedKey) {
    body.apiKey = typedKey;
  } else if (aiProviderUsesApiKey(draft.provider) && clearApiKey) {
    body.clearApiKey = true;
  }
  return body;
}

function parseTimeout(value: string, min: number, max: number, label: string): number {
  if (!/^\d+$/.test(value.trim())) {
    throw new Error(`${label}: ${min}–${max}.`);
  }
  const parsed = Number.parseInt(value.trim(), 10);
  if (parsed < min || parsed > max) {
    throw new Error(`${label}: ${min}–${max}.`);
  }
  return parsed;
}

function describeProbe(result: AiConnectionProbe, connection: AiConnection): string {
  if (
    !result.reachable &&
    aiProviderUsesApiKey(connection.provider) &&
    !connection.apiKeyConfigured
  ) {
    return 'Schlüssel fehlt.';
  }
  if (!result.reachable) {
    return 'Nicht erreichbar.';
  }
  if (connection.provider === 'grok' && result.languageModels.length === 0) {
    return 'Erreichbar. Das Konto hat keine Sprachmodelle geliefert.';
  }
  if (!result.chatModelPresent) {
    return 'Erreichbar. Das Chat-Modell fehlt.';
  }
  if (result.embeddingRejected) {
    return 'Erreichbar. Das Embedding-Modell liefert keinen Vektor mit Länge 768. Die Suche nutzt nur den Volltext.';
  }
  if (!result.embeddingModelPresent && connection.provider === 'ollama') {
    return 'Erreichbar. Das Chat-Modell ist vorhanden. Das Embedding-Modell fehlt, die Suche nutzt Volltext.';
  }
  const assist = connection.assistModel.trim();
  if (
    connection.provider === 'grok' &&
    assist.length > 0 &&
    !result.languageModels.includes(assist)
  ) {
    return 'Erreichbar. Das Chat-Modell ist vorhanden. Das Assist-Modell fehlt in der Sprachmodell-Liste.';
  }
  return 'Erreichbar. Das Chat-Modell ist vorhanden.';
}
